/**
 * Text viewer — routes to TranscriptView or PlainTextView based on document type.
 *
 * PlainTextView uses the shared line-based rendering (no TipTap).
 * TranscriptView adds speaker detection, Q/A coloring, line numbers.
 */

import React, {useMemo} from 'react';
import {DocumentRead, DocumentType} from '../../../../schemas';
import TranscriptView from './TranscriptView';
import {isLikelyTranscript} from './shared';
import {charOffsetToPageNumber} from './shared/page-mapping';
import {
    DocLine,
    HighlightOption,
    useLineSelection,
    useLineSpotlight,
    useLineHighlight,
    LineContainer,
    LineRow,
    renderHighlightedText,
} from './shared';
import './styles.css';

// ── Plain text parsing ───────────────────────────────────────────────────

function parsePlainTextLines(text: string, pageBodyOffsets?: number[]): DocLine[] {
    const lines = text.split('\n');
    let charOffset = 0;
    return lines.map((line, i) => {
        const trimmed = line.trim();
        const pageNumber = charOffsetToPageNumber(charOffset, pageBodyOffsets);
        charOffset += line.length + (i < lines.length - 1 ? 1 : 0);
        return {
            lineIndex: i,
            text: trimmed,
            lineType: trimmed ? 'content' as const : 'empty' as const,
            indent: 0,
            pageNumber,
        };
    });
}

// ── Plain text view ──────────────────────────────────────────────────────

const PlainTextView: React.FC<{
    doc: DocumentRead;
    highlightOptions: HighlightOption[];
}> = ({doc, highlightOptions}) => {
    const text = doc.body || '';
    const docLines = useMemo(() => parsePlainTextLines(text, doc.page_body_offsets), [text, doc.page_body_offsets]);

    const selection = useLineSelection(docLines);
    const spotlight = useLineSpotlight(text, docLines.length);
    const {getHighlightRanges} = useLineHighlight(highlightOptions);

    return (
        <LineContainer
            selection={selection}
            spotlight={spotlight}
            className="line-viewer--plain"
        >
            {docLines.map((line, i) => {
                const curPage = line.pageNumber;
                const prevPage = i > 0 ? docLines[i - 1]?.pageNumber : undefined;
                const showDivider = curPage != null && prevPage != null && curPage !== prevPage;
                return (
                    <React.Fragment key={i}>
                        {showDivider && (
                            <div className="page-divider" data-page={curPage}>
                                <span className="page-divider__label">Page {curPage}</span>
                            </div>
                        )}
                        <LineRow
                            index={i}
                            text={line.text}
                            isSelected={selection.selectedLines.has(i)}
                            isSpotlighted={spotlight.spotlightedLines.has(i)}
                            spotlightClass={spotlight.spotlightClass}
                            highlights={getHighlightRanges(line.text)}
                            onClick={selection.onLineClick}
                            isEmpty={line.lineType === 'empty'}
                            gutter={i + 1}
                            pageNumber={line.pageNumber}
                            renderContent={(t, hl) => renderHighlightedText(t, hl)}
                        />
                    </React.Fragment>
                );
            })}
        </LineContainer>
    );
};

// ── Exported view (routes by document type) ──────────────────────────────

const TextView: React.FC<{
    doc: DocumentRead;
    highlightOptions: HighlightOption[];
}> = ({doc, highlightOptions}) => {
    if (doc.type === DocumentType.TRANSCRIPT || isLikelyTranscript(doc.body || '')) {
        return <TranscriptView doc={doc} highlightOptions={highlightOptions}/>;
    }
    return <PlainTextView doc={doc} highlightOptions={highlightOptions}/>;
};

export default TextView;
